import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Cluster } from '../../../../resources/cluster/cluster.interface';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { FloorAreaService } from '../../../../services/floor-area/floor-area.service';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';
import { ClusterService } from '../../../../resources/cluster/cluster.service';
import { Subject } from 'rxjs';
import { BuildingService } from '../../../../resources/building/building.service';
import { Building } from '../../../../resources/building/building.interface';
import { AuthService } from '../../../../auth/auth.service';

/**
 * Cluster ratio component.
 */
@Component({
  selector: 'sg2-cluster-info-ratio',
  templateUrl: './cluster-info-ratio.component.html',
  styleUrls: ['./cluster-info-ratio.component.less']
})
export class ClusterInfoRatioComponent implements OnDestroy, OnInit {

  /** Constants. */
  private readonly DEFAULT_RATIO = 1;
  private readonly DEFAULT_RATIO_AS = 'ratio';
  private readonly DEFAULT_RATIO_BY = 'building';

  /**
   * Active cluster.
   *
   * @todo Currently the AgularJS components serves a custom version of the cluster
   *  with a lot of extra properties, so Cluster interface is only *Partial*. When
   *  migrated to Angular, it should **only** accept an actual cluster, with all
   *  areas loaded dynamically from the AreaStore.
   */
  @Input() cluster: Cluster;

  /** Dispatch action for legacy support. */
  @Output() action = new EventEmitter<{
    ratio: number,
    bvo: number
  }>();

  /** Parent building ratio as. */
  public ratioAs: Building['ratioAs'];

  /** Parent building ratio by. */
  public ratioBy: Building['ratioBy'];

  /** Destroy hook. */
  private destroy$ = new Subject<any>();

  /** Form. */
  public form: FormGroup;


  /**
   * Load the formbuilder.
   */
  constructor(
    private authService: AuthService,
    private buildingService: BuildingService,
    private clusterService: ClusterService,
    private floorAreaService: FloorAreaService,
    private formBuilder: FormBuilder
  ) { }

  /**
   * Initialize form and track form fields.
   */
  ngOnInit() {
    this._patchCluster();
    this._initForm();
    this._trackBVO();
    this._trackRatio();
    this._trackCluster();
  }

  /** Unsubscribe on destroy. */
  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Get info from parent building.
   */
  private _getParentBuildingRatio(): void {
    const building = this.buildingService.getBuildingProperties(this.cluster.buildingId);
    this.ratioAs = building.ratioAs || this.DEFAULT_RATIO_AS;
    this.ratioBy = building.ratioBy || this.DEFAULT_RATIO_BY;
  }

  /**
   * Initialize new FNO/BVO form.
   *
   * The form should only update *on blur*, to prevent unnecessary API calls.
   *
   * @todo The component works with a `Partial<Cluster>` which is not accepted by the
   *  FloorAreaService, so convert it to a regular `Cluster` before passing it as a param.
   *  This can be removed after migration to Angular.
   */
  private _initForm(): void {
    const fno = this.floorAreaService.getClusterFNO(this.cluster as Cluster);
    this.form = this.formBuilder.group({
      ratio: [ parseFloat(this.cluster.ratio.toFixed(2)), [ Validators.min(1) ]],
      fno: [{ value: parseFloat(fno.toFixed(2)), disabled: true }],
      bvo: null,
      bui: [{ value: null, disabled: true }]
    }, {
      updateOn: 'blur'
    });

    // For performance reasons it's best not to call the floorAreaService twice,
    // so patch the BVO input field with local data, and prevent triggering the
    // subscription with { emitEvent: false }, which in turn prevents the API call
    // on a form field value change.
    this.form.patchValue({
      bvo: parseFloat((this.form.controls.fno.value * this.form.controls.ratio.value).toFixed(2))
    }, {
      emitEvent: false
    });
  }

  /**
   * Determine whether to disable the form field based on parent building's `ratioAs` and `ratioBy`.
   *
   * The variables are not used, they're just there so you can call a function through ternary.
   */
  private _setDisabled(): void {
    const buildingProps = this.buildingService.getBuildingProperties(this.cluster.buildingId);
    const building = { ...buildingProps, ratioAs: buildingProps.ratioAs || this.DEFAULT_RATIO_AS };
    const ratio = (building.ratioBy !== 'cluster' || building.ratioAs !== 'ratio' || !this.authService.canUpdate('cluster'))
      ? this.form.get('ratio').disable({ emitEvent: false })
      : this.form.get('ratio').enable({ emitEvent: false });
    const bvo = (building.ratioBy !== 'cluster' || building.ratioAs !== 'bvo' || !this.authService.canUpdate('cluster'))
      ? this.form.get('bvo').disable({ emitEvent: false })
      : this.form.get('bvo').enable({ emitEvent: false });
  }

  /**
   * Patch the cluster so ratio is always available.
   *
   * @todo This should not be needed when it's either returned by default by the API,
   *  or when patched upon init in the ClusterStore/Service. Should be removed once
   *  ratio is available by default.
   */
  private _patchCluster(): void {
    this.cluster = {
      ...this.cluster,
      ratio: this.cluster.ratio || this.DEFAULT_RATIO
    };
    this._getParentBuildingRatio();
  }

  /**
   * Track cluster from the Clusters observable.
   *
   * Match the current cluster by id, so only the selected cluster instead of the whole
   * Clusters array is received.
   *
   * @todo Legacy issue: When adding a cluster from a legacy controller, it's not added to `project`, thus
   *  causing issues with retrieving the cluster from `cluster$`. When done, the fallback should be removed
   *  from this line: `this.cluster = cluster || this.cluster`. This may also cause the issues with adding
   *  a ratio or bvo to new clusters.
   */
  private _trackCluster(): void {
    this.clusterService.cluster$.pipe(
      takeUntil(this.destroy$),
      map(clusters => clusters.filter(cluster => cluster._id === this.cluster._id)[0])
    ).subscribe(cluster => {
      this.cluster = cluster || this.cluster;
      this._patchCluster();
      this._updateForm();
    });
  }

  /**
   * Track the BVO form field.
   *
   * Update the Ratio form field on updating the BVO value.
   *
   * Pipeline:
   * - Only call API when value has changed.
   * - The BVO has a min value of the current FNO (so the Ratio cannot fall below 1).
   *
   * @todo Bind to BuildingStore.
   */
  private _trackBVO(): void {
    this.form.controls.bvo.valueChanges.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(bvo => bvo < this.form.controls.fno.value ? this.form.controls.fno.value : bvo)
    ).subscribe(bvo => {
      const ratio = bvo / this.form.controls.fno.value;
      this.clusterService.setRatio(this.cluster, ratio, 'bvo');
    });
  }

  /**
   * Track the Ratio form field.
   *
   * Update the BVO form field on updating the Ratio value.
   *
   * Pipeline:
   * - Only call API when value has changed.
   * - The ratio has a min value of 1.
   *
   * @todo Bind to BuildingStore.
   */
  private _trackRatio(): void {
    this.form.controls.ratio.valueChanges.pipe(
      distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)),
      map(ratio => ratio > 1 ? ratio : 1)
    ).subscribe(ratio => {
      const bvo = parseFloat((this.form.controls.fno.value * ratio).toFixed(2));
      this.clusterService.setRatio(this.cluster, ratio, 'ratio');
    });
  }

  /**
   * Update form.
   *
   * The form is updated *after* the cluster is by patched by the API and approved by
   * the Project service.
   */
  private _updateForm(): void {
    const fno = this.clusterService.getFno(this.cluster);
    this.form.patchValue({
      ratio: parseFloat(this.cluster.ratio.toFixed(2)),
      fno: parseFloat(fno.toFixed(2)),
      bvo: parseFloat((fno * this.cluster.ratio).toFixed(2)),
      bui: this.cluster.areas
        .filter(a => a.active && a.functionality === 5)
        .reduce((sum, a) => sum + (a.surface * a.quantity), 0)
    }, {
      emitEvent: false
    });

    this._setDisabled();
  }

}
